// My Work dashboard. Six widgets in a responsive grid:
//   - My Rocks (this quarter)
//   - Mentions & Replies (last 14d)
//   - Activity · Last 24h
//   - Overdue
//   - Due this week
//   - Recently done
// Mirrors the Drillcut MyWorkDashboard. Each widget reads its own
// resource so a slow query doesn't gate the others.

import { useMemo } from "react";
import {
  Activity as ActivityIcon,
  AlarmClock,
  ArrowRight,
  AtSign,
  CalendarClock,
  CheckCheck,
  CheckSquare,
  Lightbulb,
  Target,
} from "lucide-react";
import { tpApi } from "../api";
import { useResource } from "../hooks";
import {
  currentQuarter,
  formatDateShort,
  isOverdue,
  statusChipClass,
} from "../utils";
import { Avatar } from "../components/Avatar";
import { usePlannerRefreshKey } from "../sheets/PlannerCreateContext";
import { DC } from "../sheets/createSlideOver/primitives";
import type { PlannerViewId } from "../../../../../../shared/team-planner/const";

interface Props {
  onNavigate: (view: PlannerViewId) => void;
}

export function MyWorkView({ onNavigate }: Props) {
  const quarter = currentQuarter();
  const refreshKey = usePlannerRefreshKey();
  const rocks = useResource(() => tpApi.listRocks(quarter, "mine"), [quarter, refreshKey]);
  const todos = useResource(() => tpApi.listTodos("mine"), [refreshKey]);
  const mentions = useResource(() => tpApi.listMentions(), [refreshKey]);
  const activity = useResource(() => tpApi.recentActivity(), [refreshKey]);
  const issues = useResource(() => tpApi.listIssues("open"), [refreshKey]);

  const overdueTodos = useMemo(
    () => (todos.data ?? []).filter((t) => isOverdue(t.dueAt, t.status)),
    [todos.data],
  );
  const dueThisWeek = useMemo(() => {
    const now = new Date();
    const cutoff = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    return (todos.data ?? [])
      .filter(
        (t) =>
          t.dueAt &&
          t.dueAt > now &&
          t.dueAt < cutoff &&
          t.status !== "done" &&
          t.status !== "cancelled",
      )
      .sort((a, b) => (a.dueAt!.getTime() - b.dueAt!.getTime()))
      .slice(0, 6);
  }, [todos.data]);
  const recentlyDone = useMemo(() => {
    return (todos.data ?? [])
      .filter((t) => t.status === "done" && t.completedAt)
      .sort((a, b) => (b.completedAt!.getTime() - a.completedAt!.getTime()))
      .slice(0, 6);
  }, [todos.data]);

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
        gap: 16,
      }}
    >
      <Widget
        icon={<Target size={16} />}
        title="My Rocks · this quarter"
        actionLabel="Open Rocks board"
        onAction={() => onNavigate("rocks")}
      >
        {(rocks.data ?? []).slice(0, 6).map((r) => (
          <Row
            key={r.id}
            seq={`R${r.seq}`}
            title={r.title}
            right={
              <span className={statusChipClass(r.status)}>
                {r.status.replace("_", " ")}
              </span>
            }
          />
        ))}
        {(rocks.data ?? []).length === 0 && !rocks.loading ? (
          <Empty>No rocks assigned yet.</Empty>
        ) : null}
      </Widget>

      <Widget
        icon={<AtSign size={16} />}
        title="Mentions & Replies"
        hint="Last 14 days"
      >
        {(mentions.data ?? []).slice(0, 5).map((m) => (
          <div
            key={m.id}
            style={{
              display: "flex",
              gap: 10,
              padding: "8px 10px",
              background: DC.surfaceMuted,
              borderRadius: 4,
            }}
          >
            <Avatar owner={{ displayName: m.authorDisplayName, email: null }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12 }}>
                <strong>{m.authorDisplayName ?? "Someone"}</strong>{" "}
                <span style={{ color: DC.grey }}>on a {m.itemKind}</span>
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: DC.grey,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {m.body}
              </div>
            </div>
            <span style={{ fontSize: 10, color: DC.grey }}>
              {formatDateShort(m.createdAt)}
            </span>
          </div>
        ))}
        {(mentions.data ?? []).length === 0 && !mentions.loading ? (
          <Empty>No @-mentions yet. You're in the clear.</Empty>
        ) : null}
      </Widget>

      <Widget
        icon={<AlarmClock size={16} />}
        title="Overdue"
        actionLabel="Open To-dos"
        onAction={() => onNavigate("todos")}
      >
        {overdueTodos.length === 0 ? (
          <Empty>Nothing overdue. Good.</Empty>
        ) : (
          overdueTodos.slice(0, 6).map((t) => (
            <Row
              key={t.id}
              seq={`T${t.seq}`}
              title={t.title}
              right={
                <span style={{ fontSize: 11, color: DC.redFg }}>
                  {formatDateShort(t.dueAt)}
                </span>
              }
            />
          ))
        )}
      </Widget>

      <Widget
        icon={<CalendarClock size={16} />}
        title="Due this week"
        actionLabel="Open To-dos"
        onAction={() => onNavigate("todos")}
      >
        {dueThisWeek.length === 0 ? (
          <Empty>Nothing landing this week.</Empty>
        ) : (
          dueThisWeek.map((t) => (
            <Row
              key={t.id}
              seq={`T${t.seq}`}
              title={t.title}
              right={
                <span style={{ fontSize: 11, color: DC.grey }}>
                  {formatDateShort(t.dueAt)}
                </span>
              }
            />
          ))
        )}
      </Widget>

      <Widget
        icon={<ActivityIcon size={16} />}
        title="Activity · Last 24h"
      >
        {(activity.data ?? []).slice(0, 8).map((e) => (
          <Row
            key={e.id}
            seq={
              e.itemKind === "rock" ? "R"
                : e.itemKind === "todo" ? "T"
                  : e.itemKind === "issue" ? "O"
                    : e.itemKind === "milestone" ? "M"
                      : "S"
            }
            title={`${e.actorDisplayName ?? "Someone"} ${renderVerb(e.verb)}`}
            right={
              <span style={{ fontSize: 11, color: DC.grey }}>
                {e.itemTitle ?? ""}
              </span>
            }
          />
        ))}
        {(activity.data ?? []).length === 0 && !activity.loading ? (
          <Empty>Quiet so far today.</Empty>
        ) : null}
      </Widget>

      <Widget
        icon={<CheckCheck size={16} />}
        title="Recently done"
      >
        {recentlyDone.length === 0 ? (
          <Empty>No completions in the last few days.</Empty>
        ) : (
          recentlyDone.map((t) => (
            <Row
              key={t.id}
              seq={`T${t.seq}`}
              title={t.title}
              right={
                <span style={{ fontSize: 11, color: DC.green }}>
                  {formatDateShort(t.completedAt)}
                </span>
              }
            />
          ))
        )}
      </Widget>

      {/* Keep the opportunities widget visible too so the dashboard
          reflects every primary view. */}
      <Widget
        icon={<Lightbulb size={16} />}
        title="Open opportunities"
        actionLabel="Open Opportunities"
        onAction={() => onNavigate("opportunities")}
      >
        {(issues.data ?? []).slice(0, 6).map((i) => (
          <Row
            key={i.id}
            seq={`O${i.seq}`}
            title={i.title}
            right={
              <span className="bsp-mono" style={{ fontSize: 11 }}>
                {i.voteCount} {i.voteCount === 1 ? "vote" : "votes"}
              </span>
            }
          />
        ))}
        {(issues.data ?? []).length === 0 && !issues.loading ? (
          <Empty>No open opportunities right now.</Empty>
        ) : null}
      </Widget>
    </div>
  );
}

function renderVerb(verb: string): string {
  if (verb === "created") return "created";
  if (verb === "updated") return "edited";
  if (verb === "commented") return "commented on";
  if (verb === "raised") return "raised";
  if (verb.startsWith("status:")) return `set ${verb.slice("status:".length).replace("_", " ")} on`;
  if (verb.startsWith("status-update:")) return "posted a weekly update for";
  if (verb === "primary-owner-changed") return "reassigned";
  if (verb === "converted-to-todo") return "converted to a to-do";
  if (verb === "converted-to-rock") return "converted to a rock";
  return verb;
}

function Widget({
  icon,
  title,
  hint,
  actionLabel,
  onAction,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  hint?: string;
  actionLabel?: string;
  onAction?: () => void;
  children: React.ReactNode;
}) {
  return (
    <section className="bsp-card">
      <div className="bsp-card-title-row" style={{ marginBottom: 12 }}>
        {icon}
        <h3
          style={{
            margin: 0,
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            color: "var(--bsp-dark)",
            flex: 1,
            minWidth: 0,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {title}
        </h3>
        {hint ? (
          <span style={{ fontSize: 10, color: "var(--bsp-fg-subtle)", letterSpacing: "0.06em", textTransform: "uppercase" }}>
            {hint}
          </span>
        ) : null}
        {actionLabel && onAction ? (
          <button
            type="button"
            className="bsp-btn bsp-btn-ghost bsp-btn-sm"
            onClick={onAction}
            aria-label={actionLabel}
          >
            {actionLabel} <ArrowRight size={12} />
          </button>
        ) : null}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>{children}</div>
    </section>
  );
}

function Row({
  seq,
  title,
  right,
}: {
  seq: string;
  title: string;
  right: React.ReactNode;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "8px 10px",
        background: "var(--bsp-surface-muted)",
        borderRadius: 4,
      }}
    >
      <span className="bsp-mono" style={{ fontSize: 10, color: "var(--bsp-fg-subtle)" }}>
        {seq}
      </span>
      <span
        style={{
          flex: 1,
          fontSize: 13,
          color: "var(--bsp-fg)",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {title}
      </span>
      {right}
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <p style={{ color: "var(--bsp-fg-muted)", fontSize: 12, fontStyle: "italic", margin: 0 }}>
      {children}
    </p>
  );
}
